/**
 * The overlay drawn on the "hovered class" canvas of the evaluation views.
 *
 * Hovering a topping chip outlines its bounding boxes. Hovering a sauce chip
 * paints the sauce's pixels from the ``sauce_mask`` asset instead: the mask
 * is the segmentation classmap of the ROI crop (pixel value = index into
 * ``metadata.sauce_map.classes``, background last), the same frame the
 * bounding boxes live in, so both are shifted by the ROI origin.
 *
 * Hovering a doneness gauge paints that output's heatmap layer
 * (``heatmap_burnt`` / ``heatmap_uncooked``: full frame, JET + alpha) instead.
 */

export interface Roi {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Decoded ``sauce_mask``: one class id per pixel, ROI-crop sized. */
export interface SauceMask {
  classIds: Uint8Array;
  width: number;
  height: number;
}

interface BoundingBoxLike {
  label: string;
  point1: number[] | [number, number];
  point2: number[] | [number, number];
  model_class?: string | null;
}

interface MetadataLike {
  pizza_roi?: readonly number[] | null;
  components_bbs?: BoundingBoxLike[] | null;
  sauce_map?: { classes: string[] } | null;
}

/** CSS "lime" (#00ff00), the bounding-box colour, so sauces and toppings read alike. */
export const SAUCE_TINT = { r: 0, g: 255, b: 0, a: 235 };
export const SAUCE_VEIL = "rgba(0, 0, 0, 0.45)";
export const HEATMAP_VEIL = SAUCE_VEIL;

/** Read the class ids out of the decoded mask image (grey PNG: value in the red channel). */
export function decodeSauceMask(image: HTMLImageElement): SauceMask | null {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx || !canvas.width || !canvas.height) return null;
  ctx.drawImage(image, 0, 0);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const classIds = new Uint8Array(canvas.width * canvas.height);
  for (let i = 0, p = 0; i < classIds.length; i++, p += 4) classIds[i] = data[p];
  return { classIds, width: canvas.width, height: canvas.height };
}

export function isSauceClass(modelClass?: string | null): boolean {
  return !!modelClass && modelClass.toLowerCase().endsWith("_sauce");
}

/** Order/recipe names of sauces ("bbq omáčka (zig-zag)", "sriracha", "carolina reaper"). */
export function isSauceLabel(label: string): boolean {
  return /om[aá][cč]k|sauce|sriracha|reaper/i.test(label);
}

/**
 * Background class id of the segmentation classmap (last entry of the pod's
 * class list). Used when an evaluation predates the stored legend.
 */
export const DEFAULT_BACKGROUND_ID = 9;

/**
 * Class ids (mask pixel values) of the sauces behind the hovered chip, or
 * "all" when the chip is a sauce but the evaluation carries no legend /
 * model class (evaluated before they were stored): then every non-background
 * pixel of the mask is painted.
 */
export function hoveredSauceClassIds(
  hoveredClass: string,
  metadata: MetadataLike,
): number[] | "all" {
  const classes = metadata.sauce_map?.classes;
  const bbs = (metadata.components_bbs ?? []).filter(
    (bb) => bb.label === hoveredClass,
  );
  const withClass = bbs.filter((bb) => isSauceClass(bb.model_class));
  if (classes && withClass.length) {
    const ids = new Set<number>();
    for (const bb of withClass) {
      const id = classes.findIndex(
        (c) => c.toLowerCase() === bb.model_class!.toLowerCase(),
      );
      if (id >= 0) ids.add(id);
    }
    if (ids.size) return [...ids].sort((a, b) => a - b);
  }
  const legacy = bbs.some((bb) => bb.model_class == null);
  if ((withClass.length || legacy) && isSauceLabel(hoveredClass)) return "all";
  return [];
}

const tintCache = new Map<string, HTMLCanvasElement>();

/** RGBA canvas of the mask with the given class ids painted in the sauce tint. */
function tintedMask(
  mask: SauceMask,
  ids: number[] | "all",
  cacheKey: string,
): HTMLCanvasElement {
  const key = `${cacheKey}|${mask.width}x${mask.height}|${ids === "all" ? "all" : ids.join(",")}`;
  const cached = tintCache.get(key);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = mask.width;
  canvas.height = mask.height;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(mask.width, mask.height);
  const wanted = new Uint8Array(256);
  if (ids === "all") {
    wanted.fill(1);
    wanted[DEFAULT_BACKGROUND_ID] = 0;
    wanted[255] = 0;
  } else {
    for (const id of ids) wanted[id] = 1;
  }
  const { r, g, b, a } = SAUCE_TINT;
  for (let i = 0, p = 0; i < mask.classIds.length; i++, p += 4) {
    if (wanted[mask.classIds[i]]) {
      img.data[p] = r;
      img.data[p + 1] = g;
      img.data[p + 2] = b;
      img.data[p + 3] = a;
    }
  }
  ctx.putImageData(img, 0, 0);
  if (tintCache.size > 24) tintCache.delete(tintCache.keys().next().value!);
  tintCache.set(key, canvas);
  return canvas;
}

/**
 * Draw the hover overlay for ``hoveredClass`` on a canvas that covers
 * ``paddedRoi`` of the source frame (the canvas is expected to be cleared).
 */
export function drawHoveredComponentOverlay(
  ctx: CanvasRenderingContext2D,
  {
    hoveredClass,
    metadata,
    paddedRoi,
    sauceMask,
    cacheKey,
    hoveredHeatmap,
  }: {
    hoveredClass: string | null;
    metadata: MetadataLike | null | undefined;
    paddedRoi: Roi;
    sauceMask: SauceMask | null;
    cacheKey: string;
    /** Decoded per-output doneness heatmap (full frame, JET + alpha) of a hovered gauge. */
    hoveredHeatmap?: HTMLImageElement | null;
  },
): void {
  if (hoveredHeatmap) {
    // dim the photo, then the output's response over the ROI: the JET has to pop
    ctx.fillStyle = HEATMAP_VEIL;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.drawImage(
      hoveredHeatmap,
      paddedRoi.x,
      paddedRoi.y,
      paddedRoi.width,
      paddedRoi.height,
      0,
      0,
      ctx.canvas.width,
      ctx.canvas.height,
    );
    return;
  }
  if (!hoveredClass || !metadata?.components_bbs || !metadata.pizza_roi) return;
  const [roiX, roiY] = metadata.pizza_roi;
  const offsetX = roiX - paddedRoi.x;
  const offsetY = roiY - paddedRoi.y;

  const sauceIds = hoveredSauceClassIds(hoveredClass, metadata);
  if (sauceIds !== "all" && !sauceIds.length) {
    // not a sauce: bounding boxes
  } else if (sauceMask) {
    // dim everything, then the sauce pixels in full tint: the mask has to pop
    ctx.fillStyle = SAUCE_VEIL;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.drawImage(tintedMask(sauceMask, sauceIds, cacheKey), offsetX, offsetY);
    return;
  }

  ctx.strokeStyle = "lime";
  ctx.lineWidth = 10;
  for (const bb of metadata.components_bbs) {
    if (bb.label !== hoveredClass || isSauceClass(bb.model_class)) continue;
    const [x1, y1] = bb.point1;
    const [x2, y2] = bb.point2;
    ctx.strokeRect(
      Math.min(x1, x2) + offsetX,
      Math.min(y1, y2) + offsetY,
      Math.abs(x2 - x1),
      Math.abs(y2 - y1),
    );
  }
}
